import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../ManagedCDN.css';

const DAILY_PURCHASE_URL = 'https://my.epicfinal.com/index.php?rp=/store/managed-services/devsecops-one-daily-subscription&language=hebrew&currency=2';
const WEEKLY_PURCHASE_URL = 'https://my.epicfinal.com/index.php?rp=/store/managed-services/devsecops-one-weekly-subscription&language=hebrew&currency=2';
const CONTACT_FORM_URL = '/he/consulting#epic-consulting-form';

const plans = [
    {
        scope: 'Lite',
        description: 'לעבודה נקודתית או לצורך קבוע בהיקף ממוקד.',
        options: [
            {
                type: 'חד־פעמי',
                price: '₪2,790',
                suffix: '+ מע״מ',
                text: 'למשימה, טיפול, שינוי, הקמה או עבודה מקצועית נקודתית — ללא התחייבות להמשך.',
                cta: 'הזמינו Lite חד־פעמי',
                href: DAILY_PURCHASE_URL,
                external: true,
            },
            {
                type: 'מתחדש',
                price: '₪2,320',
                suffix: '+ מע״מ לחודש',
                text: 'כתובת מקצועית קבועה לצרכים טכנולוגיים שוטפים בהיקף ממוקד.',
                cta: 'דברו איתנו על Lite מתחדש',
                href: CONTACT_FORM_URL,
            },
        ],
    },
    {
        scope: 'Core',
        description: 'לפרויקטים, לקצב עבודה גבוה יותר ולרציפות מקצועית משמעותית.',
        options: [
            {
                type: 'חד־פעמי',
                price: '₪11,600',
                suffix: '+ מע״מ',
                text: 'לפרויקט ממוקד, ספרינט, הקמה, שדרוג משמעותי, מיגרציה או משימה מורכבת.',
                cta: 'הזמינו Core חד־פעמי',
                href: WEEKLY_PURCHASE_URL,
                external: true,
            },
            {
                type: 'מתחדש',
                price: '₪9,800',
                suffix: '+ מע״מ לחודש',
                text: 'שותף טכנולוגי קבוע עם קצב עבודה גבוה יותר ורציפות לאורך זמן.',
                cta: 'דברו איתנו על Core מתחדש',
                href: CONTACT_FORM_URL,
            },
        ],
    },
];

const PlanLink = ({ option }) => {
    const className = option.external
        ? 'devsecops-plan-cta devsecops-plan-cta--primary'
        : 'devsecops-plan-cta';

    if (option.external) {
        return (
            <a href={option.href} className={className} target="_blank" rel="noopener noreferrer">
                {option.cta}
                <i className="las la-arrow-left" aria-hidden="true"></i>
            </a>
        );
    }

    return (
        <Link to={option.href} className={className}>
            {option.cta}
            <i className="las la-arrow-left" aria-hidden="true"></i>
        </Link>
    );
};

const DevSecOpsDailyHe = () => {
    return (
        <div dir="rtl">
            <Helmet>
                <title>פיתוח, אבטחה ותשתיות במנוי חודשי | DevSecOps ONE</title>
                <meta
                    name="description"
                    content="שירות DevSecOps מקצועי לפי דרישה לעסקים, במסלולי Lite או Core ובהתקשרות חד־פעמית או מתחדשת."
                />
                <html lang="he" />
            </Helmet>

            <div className="managed-cdn-page">
                <section className="cdn-hero">
                    <div className="container">
                        <div className="cdn-hero-content">
                            <div className="cdn-icon-badge">
                                <i className="las la-user-cog"></i>
                            </div>
                            <h1 className="cdn-hero-title devsecops-hero-title">פיתוח, אבטחה ותשתיות — תחת מנוי חודשי אחד</h1>
                            <div className="devsecops-hero-copy">
                                <p className="cdn-hero-subtitle">
                                    <strong>DevSecOps ONE</strong> הוא מנוי חודשי שמעניק לכם מהנדס DevSecOps ייעודי לפי דרישה.
                                </p>
                                <p className="cdn-hero-description">
                                    אתם יכולים להוסיף לתור בקשות ללא הגבלה<sup>*</sup> בתחומי פיתוח, אבטחה ותפעול,
                                    ואנחנו מקדמים אותן בצורה מסודרת — משימה פעילה אחת בכל פעם, בהתאם לסדר העדיפויות שנקבע יחד.
                                    אתם תמיד שומרים על <strong>100% בעלות</strong> על כל הקוד והתצורות שאנו מייצרים,
                                    כאשר הכול מתועד ומוכן למסירה פשוטה ונוחה.
                                </p>
                                <p className="devsecops-hero-footnote">
                                    <sup>*</sup> ביצוע הבקשות בפועל כפוף למכסת השעות החודשית שנקבעה במסלול ובהסכם ההתקשרות.
                                </p>
                            </div>
                            <div className="cdn-hero-buttons">
                                <a href="#plans" className="btn-cdn btn-primary">בחרו את המסלול שמתאים לכם</a>
                                <Link to={CONTACT_FORM_URL} className="btn-cdn btn-secondary">דברו איתנו</Link>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="devsecops-plans" id="plans">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>בחרו את הדרך שמתאימה לכם</h2>
                            <p>שתי רמות שירות, בכל אחת אפשרות חד־פעמית או שירות מתחדש.</p>
                        </div>
                        <div className="devsecops-scope-grid">
                            {plans.map((plan) => (
                                <article className="devsecops-scope-card" key={plan.scope}>
                                    <div className="devsecops-scope-header">
                                        <span>DevSecOps ONE</span>
                                        <h3>{plan.scope}</h3>
                                        <p>{plan.description}</p>
                                    </div>
                                    <div className="devsecops-options-grid">
                                        {plan.options.map((option) => (
                                            <div className="devsecops-plan-option" key={option.type}>
                                                <span className={`devsecops-option-type ${option.external ? '' : 'devsecops-option-type--recurring'}`}>
                                                    {option.type}
                                                </span>
                                                <div className="devsecops-option-price">
                                                    <strong>{option.price}</strong>
                                                    <span>{option.suffix}</span>
                                                </div>
                                                <p>{option.text}</p>
                                                <PlanLink option={option} />
                                            </div>
                                        ))}
                                    </div>
                                </article>
                            ))}
                        </div>
                        <div className="devsecops-monthly-promo">
                            <div>
                                <span>צריכים היקף עבודה חודשי?</span>
                                <p>הכירו את DevSecOps ONE לשירות שוטף, רציף ורחב יותר.</p>
                            </div>
                            <Link to="/pricing-he" className="devsecops-monthly-promo-link">
                                לפרטי המסלול החודשי
                                <i className="las la-arrow-left" aria-hidden="true"></i>
                            </Link>
                        </div>
                        <p className="devsecops-plans-note">
                            האפשרות המתחדשת היא שירות חודשי חוזר; האפשרות החד־פעמית אינה יוצרת התחייבות להמשך.
                        </p>
                    </div>
                </section>

                <section className="cdn-features">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>שירות מקצועי שמתקדם עם סדרי העדיפויות שלכם</h2>
                            <p> גישה מסודרת למומחיות DevSecOps בהתאם להיקף שבחרתם.</p>
                        </div>
                        <div className="cdn-features-grid">
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-tasks"></i></div>
                                <h3>בקשות לפי הצורך</h3>
                                <p>העבירו אלינו משימות ובקשות בהתאם לצרכים ולסדרי העדיפויות של העסק.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-code"></i></div>
                                <h3>פיתוח מותאם אישית</h3>
                                <p>פיתוח תוכנות, אינטגרציות, אוטומציות וכלים מותאמים.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-project-diagram"></i></div>
                                <h3>ייעוץ וארכיטקטורה</h3>
                                <p>תכנון פתרונות, תשתיות, ענן, ביצועים ויכולת הרחבה.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-user-check"></i></div>
                                <h3>איש מקצוע שמכיר את המערכת</h3>
                                <p>בשירות המתחדש נבנית היכרות רציפה עם הסביבה, ההיסטוריה והצרכים העסקיים.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-infinity"></i></div>
                                <h3>DevSecOps מקצה לקצה</h3>
                                <p>פיתוח, תשתיות, אוטומציה, ניטור ואבטחה תחת שירות אחד.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-shield-alt"></i></div>
                                <h3>אבטחה כחלק מהעבודה</h3>
                                <p>עקרונות אבטחה משולבים בתכנון ובביצוע, ולא מתווספים רק בסוף.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-included">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>מה אנחנו יכולים לעשות עבורכם</h2>
                            <p>מעטפת DevSecOps מקצועית בשלושה תחומים מרכזיים.</p>
                        </div>
                        <div className="cdn-included-grid">
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> פיתוח</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> פיתוח תוכנות בהתאמה אישית</li>
                                    <li><i className="las la-angle-right"></i> אינטגרציות API</li>
                                    <li><i className="las la-angle-right"></i> סקריפטים ואוטומציות</li>
                                    <li><i className="las la-angle-right"></i> תהליכי CI/CD</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> תשתיות</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> ארכיטקטורת ענן</li>
                                    <li><i className="las la-angle-right"></i> Infrastructure as Code</li>
                                    <li><i className="las la-angle-right"></i> Containers ו־Orchestration</li>
                                    <li><i className="las la-angle-right"></i> שיפור ביצועים וזמינות</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> אבטחה ותפעול</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> ביקורות אבטחה</li>
                                    <li><i className="las la-angle-right"></i> הקשחת מערכות</li>
                                    <li><i className="las la-angle-right"></i> ניטור והתראות</li>
                                    <li><i className="las la-angle-right"></i> טיפול בתקלות ואירועים</li>
                                    <li><i className="las la-angle-right"></i> שיפור תהליכי תפעול</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-cta">
                    <div className="container">
                        <div className="cdn-cta-content">
                            <h2>לא בטוחים מה מתאים לכם?</h2>
                            <p>ספרו לנו מה אתם צריכים ונעזור לבחור בין Lite ל־Core ובין התקשרות חד־פעמית למתחדשת.</p>
                            <div className="cdn-cta-buttons">
                                <Link to={CONTACT_FORM_URL} className="btn-cdn btn-light">דברו איתנו</Link>
                                <a href="#plans" className="btn-cdn btn-outline">חזרו למסלולים</a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default DevSecOpsDailyHe;
